import cn from 'classnames'
import type {
  IntegrationsContainerSliceDefaultPrimaryCategoriesItem,
  IntegrationsContainerSliceV2PrimaryCategoriesItem,
} from 'prismicio-types'
import {useEffect, useRef, useState} from 'react'
import type {TrackingProps} from 'src/common/helpers'
import {EVENTS, track} from 'src/common/helpers'
import {Icon} from 'src/common/Icon'

// Constants
const MAX_DROPDOWN_HEIGHT = 224
const ITEM_HEIGHT = 40
const DROPDOWN_MARGIN = 16

type CustomSelectProps = {
  options:
    | IntegrationsContainerSliceDefaultPrimaryCategoriesItem[]
    | IntegrationsContainerSliceV2PrimaryCategoriesItem[]
    | Array<{category_name: string; value: string}>
  activeIndex?: number
  setActiveIndex: (index: number) => void
  v2?: boolean
  trackingData: Omit<TrackingProps, 'location' | 'event'>
  // SEO-friendly navigation props
  navigationMode?: boolean
  urls?: string[]
  onNavigate?: (index: number, url: string) => void
  // Multi-select props
  multiSelect?: boolean
  activeIndices?: number[]
  setActiveIndices?: (indices: number[]) => void
  optionClassName?: string
}

type DropdownOptionProps = {
  option: {category_name: string | null; value?: string}
  index: number
  navigationMode: boolean
  url?: string
  trackingData: Omit<TrackingProps, 'location' | 'event'>
  onSelect: (index: number) => void
  onLinkClick: (e: React.MouseEvent<HTMLAnchorElement>, index: number, url: string) => void
  isSelected?: boolean
  multiSelect?: boolean
  optionClassName?: string
}

/**
 * Individual dropdown option - renders as <a> in navigation mode, <button> in filter mode
 */
const DropdownOption = ({
  option,
  index,
  navigationMode,
  url,
  trackingData,
  onSelect,
  onLinkClick,
  isSelected = false,
  multiSelect = false,
  optionClassName,
}: DropdownOptionProps) => {
  const contentClasses = 'flex w-full items-center px-3 py-2'

  if (navigationMode) {
    return (
      <a
        href={url || '#'}
        className={cn(contentClasses, 'text-grey-550 no-underline')}
        onClick={(e) => onLinkClick(e, index, url || '#')}
      >
        <span className={cn('block truncate font-normal', optionClassName)}>{option.category_name}</span>
      </a>
    )
  }

  return (
    <button
      type="button"
      className={contentClasses}
      tabIndex={-1}
      onClick={() => {
        track({
          ...trackingData,
          event: EVENTS.CLICK,
          location: `category-${option.category_name}-${index}`,
        })

        onSelect(index)
      }}
    >
      {multiSelect && (
        <span className="mr-2 flex size-4 items-center justify-center">
          {isSelected && <Icon name="check-mark-v2" className="size-4 text-moss-black" />}
        </span>
      )}
      <span className={cn('block truncate font-normal', optionClassName)}>{option.category_name}</span>
    </button>
  )
}

export const CustomSelect = ({
  options,
  activeIndex = 0,
  setActiveIndex,
  v2 = false,
  trackingData,
  navigationMode = false,
  urls = [],
  onNavigate,
  multiSelect = false,
  activeIndices = [],
  setActiveIndices,
  optionClassName,
}: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [growUp, setGrowUp] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(activeIndex)
  const [isJSEnabled, setIsJSEnabled] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Progressive enhancement: Detect JavaScript availability
  useEffect(() => {
    setIsJSEnabled(true)
  }, [])

  // Calculate dropdown direction based on available space
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownHeight = Math.min(options.length * ITEM_HEIGHT, MAX_DROPDOWN_HEIGHT)
      setGrowUp(spaceBelow < dropdownHeight + DROPDOWN_MARGIN)
    }
  }, [isOpen, options.length])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }

    return undefined
  }, [isOpen])

  // Reset highlighted option when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(activeIndex)
    }
  }, [isOpen, activeIndex])

  const handleSelectOption = (index: number) => {
    if (multiSelect && setActiveIndices) {
      // Toggle selection in multi-select mode
      const newIndices = activeIndices.includes(index)
        ? activeIndices.filter((i) => i !== index)
        : [...activeIndices, index]

      setActiveIndices(newIndices)
      // Keep dropdown open in multi-select mode
    } else {
      // Single select mode
      setActiveIndex(index)
      setIsOpen(false)
      buttonRef.current?.focus()
    }
  }

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, index: number, url: string) => {
    // Progressive enhancement: Intercept with JS if available, otherwise let link navigate naturally
    if (isJSEnabled && navigationMode && onNavigate) {
      e.preventDefault()

      track({
        ...trackingData,
        event: EVENTS.CLICK,
        location: `category-${options[index].category_name}-${index}`,
      })

      onNavigate(index, url)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()

        if (isOpen) {
          setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev))
        } else {
          setIsOpen(true)
        }

        break

      case 'ArrowUp':
        e.preventDefault()

        if (isOpen) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        } else {
          setIsOpen(true)
        }

        break

      case 'Enter':
      case ' ':
        e.preventDefault()

        if (isOpen) {
          handleSelectOption(highlightedIndex)
        } else {
          setIsOpen(true)
        }

        break

      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        break

      case 'Home':
        e.preventDefault()

        if (isOpen) {
          setHighlightedIndex(0)
        }

        break

      case 'End':
        e.preventDefault()

        if (isOpen) {
          setHighlightedIndex(options.length - 1)
        }

        break
    }
  }

  // Style classes based on v2 prop
  const buttonClasses = cn(
    'relative size-full cursor-default rounded-[4px] border bg-moss-white pr-10 text-left text-moss-black focus:outline focus:outline-1 focus:outline-[#94CC32] sm:leading-6',
    {
      'text-body-m border-grey-150 shadow-sm pl-3 py-1.5': !v2,
      'text-tg-body-m border-black/[10%] pl-4 h-9 py-[7px]': v2,
    },
  )

  const linkClasses = (isActive: boolean) =>
    cn('block rounded-[4px] border px-3 py-2 no-underline transition-colors', {
      'text-body-m border-grey-150': !v2,
      'text-tg-body-m border-black/[10%]': v2,
      'bg-grey-120 border-[#94CC32]': isActive,
      'bg-moss-white text-moss-black hover:bg-grey-110': !isActive,
    })

  const dropdownClasses = cn(
    `absolute ${
      growUp ? 'bottom-full top-auto mb-2' : 'top-full mt-1'
    } right-0 z-10 w-full overflow-auto rounded-lg bg-moss-white py-1 shadow-lg ring-1 ring-grey-150 transition-all duration-300 focus:outline-none`,
    {
      'max-h-0 opacity-0': !isOpen,
      'max-h-56 opacity-100': isOpen,
      'text-body-m': !v2,
      'text-tg-body-m': v2,
    },
  )

  const chevronClasses = cn('scale-y-flip text-moss-black transition-all duration-300', {
    'rotate-180': isOpen,
    'h-3.5 w-3.5': !v2,
    'h-1.5 w-2.5': v2,
  })

  const shouldShowEnhancedDropdown = !navigationMode || isJSEnabled

  // Get button display text for multi-select
  const getButtonText = () => {
    if (!multiSelect) {
      return options[activeIndex]?.category_name
    }

    if (activeIndices.length === 0) {
      return options[0]?.category_name // Show "All" option
    }

    if (activeIndices.length === 1) {
      return options[activeIndices[0]]?.category_name
    }

    return `${activeIndices.length} selected`
  }

  return (
    <div className="w-full" ref={dropdownRef}>
      <div className={`relative h-full ${v2 ? 'w-full' : 'w-[120px] max-w-[120px]'}`}>
        {/*
          SEO Layer: Crawlable navigation links
          - Visible without JavaScript for bots and no-JS users
          - Hidden (sr-only) when JavaScript loads
          - Only rendered in navigation mode
        */}
        {navigationMode && urls.length > 0 && (
          <nav
            className={cn('flex w-full flex-col gap-2', {
              'sr-only': isJSEnabled,
            })}
            aria-label="Category navigation"
          >
            {options.map((option, index) => (
              <a
                key={`seo-link-${index}`}
                href={urls[index] || '#'}
                className={linkClasses(activeIndex === index)}
                onClick={(e) => handleLinkClick(e, index, urls[index] || '#')}
              >
                {option.category_name}
              </a>
            ))}
          </nav>
        )}

        {/*
          Enhanced Layer: Custom dropdown
          - Shown when JS is enabled (navigation mode) OR always (filter mode)
          - Provides better UX with keyboard navigation and visual feedback
        */}
        {shouldShowEnhancedDropdown && (
          <>
            <button
              ref={buttonRef}
              type="button"
              className={buttonClasses}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-activedescendant={isOpen ? `listbox-option-${highlightedIndex}` : undefined}
              onClick={() => setIsOpen(!isOpen)}
              onKeyDown={handleKeyDown}
            >
              <span className="flex items-center">
                <span className="block truncate">{getButtonText()}</span>
              </span>
              <span
                className={cn('pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center', {
                  'pr-2': !v2,
                  'pr-4': v2,
                })}
              >
                <Icon name="chevron-v2" className={chevronClasses} />
              </span>
            </button>

            <ul
              className={dropdownClasses}
              style={{visibility: isOpen ? 'visible' : 'hidden'}}
              tabIndex={-1}
              role="listbox"
            >
              {options.map((option, index) => {
                const isActive = multiSelect ? activeIndices.includes(index) : activeIndex === index
                const isHighlighted = highlightedIndex === index

                return (
                  <li
                    className={cn('relative cursor-default select-none text-grey-550', {
                      'bg-grey-120': isActive || isHighlighted,
                      'bg-moss-white hover:bg-grey-110': !isActive && !isHighlighted,
                    })}
                    id={`listbox-option-${index}`}
                    role="option"
                    aria-selected={isActive}
                    key={index}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <DropdownOption
                      option={option}
                      index={index}
                      navigationMode={navigationMode}
                      url={urls[index]}
                      trackingData={trackingData}
                      onSelect={handleSelectOption}
                      onLinkClick={handleLinkClick}
                      isSelected={isActive}
                      multiSelect={multiSelect}
                      optionClassName={optionClassName}
                    />
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
